// Hand-maintained mirror of models/src/khorium_models/structural_material_library.py
// Sync verified by KhoriumBackend/tests/shared/test_structural_material_library_sync.py.
// Edit the Python source first; this file follows.

export interface StructuralProperties {
  E_MPa: number;
  nu: number;
  density: number;
  yield_MPa: number;
  ultimate_MPa: number;
  /**
   * Coefficient of linear thermal expansion [1/K] at ~20 C. Drives
   * thermal-shrink bolt preload (ENG-2150), where a bolt-only temperature drop
   * generates tension. The temperature there is fictitious — see the Python
   * source for why a single value is exact rather than approximate in a linear
   * elastic run.
   */
  alpha_per_K: number;
  /** Heat-treatment/temper/processing state the numbers are valid for. */
  condition: string;
  /** Plain-language expansion of an abbreviated/proprietary material name. */
  description?: string;
}

export const STRUCTURAL_MATERIALS: Record<string, StructuralProperties> = {
  steel_304: {
    E_MPa: 193_000,
    nu: 0.29,
    density: 8000,
    yield_MPa: 215,
    ultimate_MPa: 505,
    alpha_per_K: 1.73e-5,
    condition: 'Annealed',
  },
  carbon_steel_1045: {
    E_MPa: 200_000,
    nu: 0.3,
    density: 7850,
    yield_MPa: 530,
    ultimate_MPa: 625,
    alpha_per_K: 1.15e-5,
    condition: 'Cold drawn',
  },
  steel_structural: {
    E_MPa: 200_000,
    nu: 0.3,
    density: 7850,
    yield_MPa: 250,
    ultimate_MPa: 400,
    alpha_per_K: 1.17e-5,
    condition:
      'Generic / as-rolled — not tied to a specific temper (approximates ASTM A36-class structural steel)',
  },
  aluminum_6061: {
    E_MPa: 68_900,
    nu: 0.33,
    density: 2700,
    yield_MPa: 276,
    ultimate_MPa: 310,
    alpha_per_K: 2.36e-5,
    condition: 'T6',
  },
  aluminum_7075: {
    E_MPa: 71_700,
    nu: 0.33,
    density: 2810,
    yield_MPa: 503,
    ultimate_MPa: 572,
    alpha_per_K: 2.34e-5,
    condition: 'T6',
  },
  titanium_ti6al4v: {
    E_MPa: 113_800,
    nu: 0.34,
    density: 4430,
    yield_MPa: 880,
    ultimate_MPa: 950,
    alpha_per_K: 8.6e-6,
    condition: 'Annealed',
  },
  copper_c110: {
    E_MPa: 117_000,
    nu: 0.34,
    density: 8940,
    yield_MPa: 70,
    ultimate_MPa: 220,
    alpha_per_K: 1.7e-5,
    condition: 'Annealed (soft)',
  },
  brass_c360: {
    E_MPa: 97_000,
    nu: 0.31,
    density: 8500,
    yield_MPa: 124,
    ultimate_MPa: 338,
    alpha_per_K: 2.05e-5,
    condition: 'Generic / typical value — not tied to a specific named temper',
  },
  inconel_718: {
    E_MPa: 200_000,
    nu: 0.29,
    density: 8190,
    yield_MPa: 1_034,
    ultimate_MPa: 1_240,
    alpha_per_K: 1.3e-5,
    condition:
      'Solution annealed + aged (precipitation hardened, AMS 5662-class)',
    description: 'Nickel-chromium superalloy',
  },
  pcabs: {
    E_MPa: 2_500,
    nu: 0.4,
    density: 1130,
    yield_MPa: 50,
    ultimate_MPa: 60,
    alpha_per_K: 8e-5,
    condition: 'As molded (unreinforced, injection molded)',
    description: 'Polycarbonate–ABS blend',
  },
  polycarbonate: {
    E_MPa: 2_400,
    nu: 0.37,
    density: 1200,
    yield_MPa: 65,
    ultimate_MPa: 70,
    alpha_per_K: 6.5e-5,
    condition: 'As molded (unreinforced, injection molded)',
  },
  abs: {
    E_MPa: 2_300,
    nu: 0.35,
    density: 1050,
    yield_MPa: 41,
    ultimate_MPa: 45,
    alpha_per_K: 9e-5,
    condition: 'As molded (unreinforced, injection molded)',
    description: 'Acrylonitrile butadiene styrene',
  },
  nylon_66: {
    E_MPa: 2_800,
    nu: 0.39,
    density: 1140,
    yield_MPa: 75,
    ultimate_MPa: 85,
    alpha_per_K: 8e-5,
    condition: 'Dry as molded (DAM)',
  },
  peek: {
    E_MPa: 3_600,
    nu: 0.39,
    density: 1320,
    yield_MPa: 100,
    ultimate_MPa: 110,
    alpha_per_K: 4.7e-5,
    condition: 'As molded (unreinforced, injection molded)',
    description: 'Polyether ether ketone',
  },
  ptfe: {
    E_MPa: 500,
    nu: 0.46,
    density: 2200,
    yield_MPa: 9,
    ultimate_MPa: 25,
    alpha_per_K: 0.000135,
    condition: 'As molded (virgin/unfilled)',
    description: 'Polytetrafluoroethylene',
  },
  hdpe: {
    E_MPa: 1_000,
    nu: 0.45,
    density: 950,
    yield_MPa: 26,
    ultimate_MPa: 33,
    alpha_per_K: 0.00012,
    condition: 'As molded (unreinforced, injection molded)',
    description: 'High-density polyethylene',
  },
  // ── ENG-3085 pack (thermal + electrostatic dielectrics / metals) ───────
  alumina: {
    // 96% Al₂O₃ ceramic — tensile strength is the limiting figure.
    E_MPa: 300_000,
    nu: 0.22,
    density: 3900,
    yield_MPa: 220,
    ultimate_MPa: 300,
    alpha_per_K: 8e-6,
    condition: 'As-fired 96% alumina ceramic',
    description: 'Aluminum oxide ceramic (Al₂O₃)',
  },
  silicone: {
    // Soft silicone rubber / PDMS-class — electrostatic housing / gasket.
    E_MPa: 20,
    nu: 0.49,
    density: 1100,
    yield_MPa: 5,
    ultimate_MPa: 8,
    alpha_per_K: 0.0002,
    condition: 'As molded (unreinforced silicone rubber)',
    description: 'Silicone rubber elastomer',
  },
  acrylic: {
    // Cast / extruded PMMA — common transparent electrostatic dielectric.
    E_MPa: 3_000,
    nu: 0.35,
    density: 1180,
    yield_MPa: 70,
    ultimate_MPa: 75,
    alpha_per_K: 7e-5,
    condition: 'As cast / extruded (unreinforced PMMA)',
    description: 'Polymethyl methacrylate (PMMA)',
  },
  aluminum_5052: {
    E_MPa: 70_300,
    nu: 0.33,
    density: 2680,
    yield_MPa: 193,
    ultimate_MPa: 228,
    alpha_per_K: 2.38e-5,
    condition: 'H32',
  },
  tungsten: {
    E_MPa: 400_000,
    nu: 0.28,
    density: 19300,
    yield_MPa: 750,
    ultimate_MPa: 980,
    alpha_per_K: 4.5e-6,
    condition: 'Sintered / wrought pure tungsten',
  },
};

export const STRUCTURAL_MATERIAL_LABELS: Record<string, string> = {
  steel_304: 'Steel 304',
  carbon_steel_1045: 'Carbon Steel 1045',
  steel_structural: 'Structural Steel',
  aluminum_6061: 'Aluminum 6061',
  aluminum_7075: 'Aluminum 7075',
  titanium_ti6al4v: 'Titanium Ti-6Al-4V',
  copper_c110: 'Copper C110',
  brass_c360: 'Brass C360',
  inconel_718: 'Inconel 718',
  pcabs: 'PC/ABS',
  polycarbonate: 'Polycarbonate',
  abs: 'ABS',
  nylon_66: 'Nylon 66',
  peek: 'PEEK',
  ptfe: 'PTFE',
  hdpe: 'HDPE',
  alumina: 'Alumina',
  silicone: 'Silicone',
  acrylic: 'Acrylic',
  aluminum_5052: 'Aluminum 5052',
  tungsten: 'Tungsten',
};
